"use client"

import * as React from "react"
import { Check, ChevronsUpDown, X, SearchX } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
  description?: string  // Optional: for showing IDs, subtitles, etc.
  icon?: React.ComponentType<{ className?: string }>  // Optional: icon component
  iconClassName?: string  // Optional: custom className for icon (e.g., colors)
}

// Single select props
interface SingleSelectProps {
  mode?: "single"
  value?: string
  onValueChange: (value: string | undefined) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  allowClear?: boolean
}

// Multi select props
interface MultiSelectProps {
  mode: "multiple"
  value?: string[]
  onValueChange: (value: string[]) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  maxDisplay?: number  // Max badges to show before "+X more"
}

type ComboboxProps = SingleSelectProps | MultiSelectProps

export function Combobox(props: ComboboxProps) {
  const {
    options,
    placeholder = "Select option...",
    searchPlaceholder = "Search...",
    emptyMessage = "No option found.",
    className,
    disabled = false,
  } = props

  const [open, setOpen] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)

  // Prevent parent scroll when scrolling inside combobox
  React.useEffect(() => {
    if (!open) return

    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation()
    }

    const element = contentRef.current
    if (element) {
      element.addEventListener('wheel', handleWheel, { passive: false })
      return () => element.removeEventListener('wheel', handleWheel)
    }
  }, [open])

  // Single select mode
  if (props.mode !== "multiple") {
    const { value, onValueChange, allowClear = false } = props
    const selectedOption = options.find((option) => option.value === value)

    const handleSelect = (currentValue: string) => {
      if (currentValue === value && allowClear) {
        onValueChange(undefined)
      } else {
        onValueChange(currentValue === value ? undefined : currentValue)
      }
      setOpen(false)
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
          className={cn(
            "w-full h-9 justify-between",
            !value && "text-muted-foreground",
            className
          )}
          >
            <div className="flex items-center gap-2 truncate">
              {selectedOption?.icon && (
                <selectedOption.icon className={cn("h-4 w-4 shrink-0", selectedOption.iconClassName)} />
              )}
              <span className="truncate">
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          ref={contentRef}
          className="w-[var(--radix-popover-trigger-width)] p-0" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>
                <div className="flex flex-col items-center justify-center gap-2">
                  <SearchX className="h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm font-medium">{emptyMessage}</p>
                </div>
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label, option.description || ""]}
                    onSelect={handleSelect}
                    className="cursor-pointer flex items-start justify-between"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {option.icon && (
                        <option.icon className={cn("h-4 w-4 shrink-0", option.iconClassName)} />
                      )}
                      {option.description ? (
                        <div className="flex flex-col items-start flex-1 min-w-0 pr-2">
                          <span className="truncate w-full" title={option.label}>
                            {option.label}
                          </span>
                          <span className="text-xs text-muted-foreground font-mono mt-0.5 break-all" title={option.description}>
                            {option.description}
                          </span>
                        </div>
                      ) : (
                        <span className="truncate">{option.label}</span>
                      )}
                    </div>
                    <Check
                      className={cn(
                        "ml-2 h-4 w-4 shrink-0 mt-0.5",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  // Multi select mode
  const { value = [], onValueChange, maxDisplay = 3 } = props
  const selectedOptions = options.filter((option) => value.includes(option.value))

  const handleSelect = (currentValue: string) => {
    const newValue = value.includes(currentValue)
      ? value.filter((v) => v !== currentValue)
      : [...value, currentValue]
    onValueChange(newValue)
  }

  const handleRemove = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onValueChange(value.filter((v) => v !== valueToRemove))
  }

  const displayCount = selectedOptions.length
  const visibleOptions = selectedOptions.slice(0, maxDisplay)
  const remainingCount = displayCount - maxDisplay

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full min-h-9 h-auto justify-between",
            value.length === 0 && "text-muted-foreground",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            {value.length === 0 ? (
              <span>{placeholder}</span>
            ) : (
              <>
                {visibleOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {option.label}
                    <span
                      role="button"
                      tabIndex={0}
                      className="ml-1 ring-offset-background rounded-sm hover:bg-muted cursor-pointer inline-flex items-center"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => handleRemove(option.value, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleRemove(option.value, e as any)
                        }
                      }}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </span>
                  </Badge>
                ))}
                {remainingCount > 0 && (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    +{remainingCount} more
                  </Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        ref={contentRef}
        className="w-[var(--radix-popover-trigger-width)] p-0" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>
              <div className="flex flex-col items-center justify-center gap-2">
                <SearchX className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium">{emptyMessage}</p>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, option.description || ""]}
                  onSelect={handleSelect}
                  className="cursor-pointer flex items-start justify-between"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {option.icon && (
                      <option.icon className={cn("h-4 w-4 shrink-0", option.iconClassName)} />
                    )}
                    {option.description ? (
                      <div className="flex flex-col items-start flex-1 min-w-0 pr-2">
                        <span className="truncate w-full" title={option.label}>
                          {option.label}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono mt-0.5 break-all" title={option.description}>
                          {option.description}
                        </span>
                      </div>
                    ) : (
                      <span className="truncate">{option.label}</span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4 shrink-0 mt-0.5",
                      value.includes(option.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
